import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Layout } from "../components/Navbar";
import { api, getUser, canClinical } from "../api";
import { toast } from "sonner";
import { MedicalField, BloodSelect, bloodLabel } from "../components/BioFields";

const CLINICAL = ["conditions", "allergies", "medications", "surgeries", "family_history"];

function ReadField({ label, v, testid, NS }) {
  return (<div><label className="text-sm font-medium">{label}</label>
    <div className="mt-1 break-words" data-testid={testid}>{v || NS}</div></div>);
}

export default function HealthBio() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const [data, setData] = useState(null);
  const [id, setId] = useState({});
  const [b, setB] = useState({ field_status: {} });
  useEffect(() => {
    const url = patientId ? `/users/${patientId}/health-bio` : "/health-bio";
    api.get(url).then(({ data }) => { setData(data); setId(data.identity || {}); setB({ field_status: {}, ...(data.bio || {}) }); })
      .catch((e) => {
        const st = e.response?.status;
        // Errore di autorizzazione/not-found: NON renderizzare i campi come "Non inserito" (non sono dati assenti, è accesso negato)
        if (st === 403 || st === 404) { setData({ denied: true }); return; }
        toast.error(e.response?.data?.detail || "Errore"); setData({ editable: false, identity: null, bio: {} });
      });
  }, [patientId]);
  const editable = !!data?.editable;
  const setVal = (k, v) => setB((p) => ({ ...p, [k]: v, field_status: { ...p.field_status, [k]: undefined } }));
  const setStat = (k, st) => setB((p) => ({ ...p, [k]: "", field_status: { ...p.field_status, [k]: st || undefined } }));
  const saveBio = async () => {
    try { await api.put("/health-bio", b); toast.success(t("hbio.saved")); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  if (data === null) return <Layout><div className="yp-container py-8">{t("common.loading")}</div></Layout>;
  if (data.denied) return (<Layout><div className="yp-container max-w-2xl py-8" data-testid="health-bio-denied">
    {patientId && <Link to={`/chat/${patientId}`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4" data-testid="bio-back-chat"><ArrowLeft size={16} aria-hidden="true" /> {t("followx.back_to_chat")}</Link>}
    <h1 className="text-3xl font-bold mb-2">{t("hbio.title")}</h1>
    <div className="yp-card p-6 border border-red-200 bg-red-50 text-red-700" data-testid="health-bio-denied-msg">{t("hbio.denied")}</div>
  </div></Layout>);
  const NS = <span className="text-slate-400 italic">{t("hbio.not_set")}</span>;

  // Visualizzazione read-only (professionista o non modificabile): 'todo' -> "Non indicato..."/"Da completare", mai "Nessuno"
  const readVal = (k) => {
    const st = b.field_status?.[k];
    if (st === "todo") return <span className="text-slate-500 italic" data-testid={`view-bio-${k}-todo`}>{patientId ? t("bio.pro_todo") : t("bio.todo_badge")}</span>;
    if (st === "none") return <span data-testid={`view-bio-${k}-none`}>{t("bio.opt_none")}</span>;
    const v = b[k];
    if (!v) return NS;
    return <span>{k === "blood_type" ? bloodLabel(v) : v}</span>;
  };

  return (<Layout><div className="yp-container max-w-2xl py-8" data-testid="health-bio-page">
    {patientId && (
      <div className="flex items-center gap-4 mb-4 flex-wrap" data-testid="bio-nav">
        <Link to={`/chat/${patientId}`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="bio-back-chat"><ArrowLeft size={16} aria-hidden="true" /> {t("followx.back_to_chat")}</Link>
        {canClinical(getUser()) && <Link to={`/clinica/${patientId}`} className="text-sm text-blue-600 hover:underline" data-testid="bio-clinical-link">{t("followx.clinical")}</Link>}
      </div>)}
    <h1 className="text-3xl font-bold mb-2">{t("hbio.title")}</h1>
    <div className="yp-card p-6 space-y-4">
      <h2 className="font-semibold" data-testid="hbio-identity-title">{t("hbio.identity_title")}</h2>
      <div className="grid md:grid-cols-2 gap-3">
        <ReadField label={t("hbio.first")} v={id.first_name} testid="hbio-first" NS={NS} />
        <ReadField label={t("hbio.last")} v={id.last_name} testid="hbio-last" NS={NS} />
        <ReadField label={t("hbio.tax_id")} v={id.tax_id} testid="hbio-taxid" NS={NS} />
        <ReadField label={t("hbio.birth_date")} v={id.birth_date} testid="hbio-bdate" NS={NS} />
        <div><label className="text-sm font-medium">{t("hbio.age")}</label>
          <div className="mt-1" data-testid="hbio-age">{id.age != null ? id.age : NS}</div></div>
      </div>
    </div>
    <div className="yp-card p-6 space-y-5 mt-4">
      {editable ? (<>
        {CLINICAL.map((k) => (
          <MedicalField key={k} fieldKey={k} multiline value={b[k]} status={b.field_status?.[k]}
            onValue={(v)=>setVal(k,v)} onStatus={(s)=>setStat(k,s)} options={["none","todo"]} />
        ))}
        <MedicalField fieldKey="height" value={b.height} status={b.field_status?.height}
          onValue={(v)=>setVal("height",v)} onStatus={(s)=>setStat("height",s)} options={["todo"]} />
        <MedicalField fieldKey="weight" value={b.weight} status={b.field_status?.weight}
          onValue={(v)=>setVal("weight",v)} onStatus={(s)=>setStat("weight",s)} options={["todo"]} />
        <BloodSelect value={b.blood_type} status={b.field_status?.blood_type}
          onChange={(v)=> v === "__todo__" ? setStat("blood_type","todo") : setVal("blood_type", v)} />
        <div className="space-y-1">
          <label htmlFor="bio-field-notes" className="block text-sm font-medium">{t("bio.notes")}</label>
          <textarea id="bio-field-notes" className="yp-input w-full" rows={2} value={b.notes || ""} onChange={e=>setVal("notes",e.target.value)} data-testid="edit-bio-notes" />
        </div>
        <button className="yp-btn yp-btn-primary w-full" onClick={saveBio} data-testid="save-health-bio-btn">{t("common.save")}</button>
      </>) : (
        [...CLINICAL, "height", "weight", "blood_type", "notes"].map((k) => (
          <div key={k}><label className="text-sm font-medium">{k === "blood_type" ? t("bio.blood_label") : t(`bio.${k}`)}</label>
            <div className="mt-1 break-words" data-testid={`view-bio-${k}`}>{readVal(k)}</div></div>
        ))
      )}
    </div>
  </div></Layout>);
}
